import React, { useState } from 'react';
import { Heart, ChevronDown, LogOut, Loader2, LogIn, Settings, Coins, ShieldAlert } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { AccountSettingsDialog } from './AccountSettings';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { InitialsAvatar } from './InitialsAvatar';
import { useAuth } from '../auth';
import { useLang } from '../i18n';

/**
 * Header account control. The whole app is behind login, so an authenticated
 * user is always present inside the app shell: we show the account + logout.
 * When the (dormant) paywall is enabled, the premium entry point is shown too.
 */
export const AccountMenu = () => {
  const { user, logout, promptLogin } = useAuth();
  const { lang } = useLang();
  const de = lang === 'de';
  const [busy, setBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleLogout = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await logout();
    } catch (e) { /* ignore */ }
    setBusy(false);
  };

  // Guest: show a prominent "Sign in" button that opens the login dialog.
  if (!user) {
    return (
      <button
        type="button"
        data-testid="header-login-btn"
        onClick={() => promptLogin()}
        className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-2.5 py-1.5 text-sm font-semibold text-primary-foreground shadow-sm transition-opacity duration-150 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:px-3"
      >
        <LogIn className="h-4 w-4" />
        <span className="hidden sm:inline">{de ? 'Anmelden' : 'Sign in'}</span>
      </button>
    );
  }

  const displayName = (user && (user.name || user.email)) || (de ? 'Konto' : 'Account');
  const avatarSeed = (user && (user.username || user.name || user.email)) || '?';
  const avatar = user.picture ? (
    <Avatar className="h-5 w-5">
      <AvatarImage src={user.picture} alt={displayName} referrerPolicy="no-referrer" />
      <AvatarFallback className="p-0 text-[10px]"><InitialsAvatar seed={avatarSeed} color={user.avatar_color} size={20} /></AvatarFallback>
    </Avatar>
  ) : (
    <InitialsAvatar seed={avatarSeed} color={user.avatar_color} size={20} />
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="account-menu-btn"
            className="inline-flex items-center gap-1.5 rounded-lg border bg-card px-2.5 py-1.5 text-sm font-medium text-muted-foreground shadow-sm transition-colors duration-150 hover:text-foreground hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {avatar}
            <span className="hidden max-w-[10rem] truncate sm:inline" data-testid="auth-user-email">{displayName}</span>
            <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            {user?.name ? <span className="truncate font-semibold text-foreground" data-testid="account-menu-name">{user.name}</span> : null}
            {user?.username ? <span className="truncate text-xs font-normal text-primary" data-testid="account-menu-username">@{user.username}</span> : null}
            <span className="truncate text-xs font-normal text-muted-foreground">{user ? (user.email || displayName) : displayName}</span>
          </DropdownMenuLabel>
          {user && (user.recent_failed_logins || 0) > 0 ? (
            <>
              <DropdownMenuSeparator />
              <div className="flex items-start gap-2 px-2 py-1.5 text-xs text-amber-600" data-testid="account-menu-failed-logins">
                <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  {de
                    ? `${user.recent_failed_logins} fehlgeschlagene Anmeldeversuche vor deiner letzten Anmeldung.`
                    : `${user.recent_failed_logins} failed sign-in attempts before your last login.`}
                </span>
              </div>
            </>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setSettingsOpen(true)} data-testid="account-points-item">
            <Coins className="mr-2 h-4 w-4 text-primary" />
            {user?.points_unlimited
              ? (de ? 'Punkte: Unbegrenzt' : 'Points: Unlimited')
              : (de ? `Punkte: ${user?.points ?? 0}` : `Points: ${user?.points ?? 0}`)}
          </DropdownMenuItem>
          <DropdownMenuItem asChild data-testid="account-supporter-item">
            <a href="/supporter">
              <Heart className="mr-2 h-4 w-4 text-primary" />
              {de ? 'Unterstützen' : 'Support us'}
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setSettingsOpen(true)} data-testid="account-settings-item">
            <Settings className="mr-2 h-4 w-4" />
            {de ? 'Konto-Einstellungen' : 'Account settings'}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleLogout} data-testid="auth-logout-btn" className="text-destructive focus:text-destructive">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LogOut className="mr-2 h-4 w-4" />}
            {de ? 'Abmelden' : 'Sign out'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AccountSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
};

export default AccountMenu;
